/** Step 1 contact / representative field rules (aligned with backend). */

const PHONE_LENGTH_RULES: Record<string, { min: number; max: number }> = {
  "34": { min: 9, max: 9 },
  "91": { min: 10, max: 10 },
  "86": { min: 11, max: 11 },
  "52": { min: 10, max: 10 },
  "55": { min: 11, max: 11 },
  "54": { min: 10, max: 11 },
  "57": { min: 10, max: 10 },
  "51": { min: 9, max: 9 },
  "56": { min: 9, max: 9 },
  "971": { min: 9, max: 9 },
  "966": { min: 9, max: 9 },
  "974": { min: 8, max: 8 },
  "965": { min: 8, max: 8 },
  "972": { min: 9, max: 9 },
  "962": { min: 9, max: 9 },
  "44": { min: 10, max: 10 },
  "33": { min: 9, max: 9 },
  "49": { min: 10, max: 11 },
  "39": { min: 9, max: 10 },
  "61": { min: 9, max: 10 },
  "212": { min: 9, max: 9 },
  "1": { min: 10, max: 10 },
};

const MIDDLE_EAST_DIAL_CODES = new Set(["971", "966", "974", "965", "972", "962"]);
const LATAM_DIAL_CODES = new Set(["52", "55", "54", "57", "51", "56"]);

function digitsOnly(value: string): string {
  return String(value || "").replace(/\D/g, "");
}

export function getPhoneLengthRule(countryCode: string): { min: number; max: number } {
  const cleanDial = digitsOnly(countryCode);
  if (PHONE_LENGTH_RULES[cleanDial]) return PHONE_LENGTH_RULES[cleanDial];
  if (MIDDLE_EAST_DIAL_CODES.has(cleanDial)) return { min: 8, max: 9 };
  if (LATAM_DIAL_CODES.has(cleanDial)) return { min: 9, max: 11 };
  return { min: 8, max: 11 };
}

/** Total digits in international number (matches applicant `toE164` composition). */
export function totalPhoneDigits(countryCode: string, nationalRaw: string): number {
  const raw = String(nationalRaw ?? "").trim();
  if (!raw) return 0;
  const compact = raw.replace(/[^\d+]/g, "");
  if (compact.startsWith("+")) {
    return compact.replace(/\D/g, "").length;
  }
  const cc = String(countryCode ?? "").replace(/[^\d+]/g, "") || "+34";
  const ccDigits = cc.replace(/\D/g, "");
  const natDigits = compact.replace(/\D/g, "");
  return (ccDigits + natDigits).length;
}

/** E.164-style: 7–15 digits total (ITU). National part max aligns with dial code length. */
export function isValidRequiredPhoneParts(countryCode: string, nationalRaw: string): boolean {
  const raw = String(nationalRaw ?? "").trim();
  if (!raw) return false;
  const nationalDigits = digitsOnly(raw);
  const { min, max } = getPhoneLengthRule(countryCode);
  return nationalDigits.length >= min && nationalDigits.length <= max;
}

/** Optional second phone: empty OK; otherwise same digit bounds as main phone. */
export function isValidOptionalPhoneParts(countryCode: string, nationalRaw: string): boolean {
  const raw = String(nationalRaw ?? "").trim();
  if (!raw) return true;
  const nationalDigits = digitsOnly(raw);
  const { min, max } = getPhoneLengthRule(countryCode);
  return nationalDigits.length >= min && nationalDigits.length <= max;
}

export function isValidEmail(s: string): boolean {
  const normalized = String(s ?? "").trim().toLowerCase();
  if (normalized.length < 6 || normalized.length > 100) return false;
  return /^(?!.*\.\.)([A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*)@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(normalized);
}

export function normalizeNieValue(value: string): string {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 9);
}

export function isValidOptionalNie(value: string): boolean {
  const normalized = normalizeNieValue(value);
  if (!normalized) return true;
  if (!/^[XYZ]\d{7}[A-Z]$/.test(normalized)) return false;
  const letters = "TRWAGMYFPDXBNJZSQVHLCKE";
  const prefixMap: Record<string, string> = { X: "0", Y: "1", Z: "2" };
  const numeric = `${prefixMap[normalized[0]]}${normalized.slice(1, 8)}`;
  const expected = letters[Number(numeric) % 23];
  return normalized[8] === expected;
}

/** Unicode-aware name: letters, marks, spaces, hyphens, apostrophes; no digits. */
export function isValidNameField(s: string, required = true): boolean {
  const normalized = String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!normalized) return !required;
  if (normalized.length > 40) return false;
  return /^[\p{L}\p{M}\s'’-]+$/u.test(normalized);
}

/**
 * Birth-certificate style full name (e.g. parent): 1–maxLen chars when required;
 * same character rules as `isValidNameField`. Optional when `required` is false.
 */
export function isValidCertificateName(
  s: string,
  maxLen: number,
  required: boolean
): boolean {
  const normalized = String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!normalized) return !required;
  if (normalized.length < 1 || normalized.length > maxLen) return false;
  return /^[\p{L}\p{M}\s'’-]+$/u.test(normalized);
}
